/*
 * Head-to-head "tug-of-war" bars — Team A vs Team B on one metric each. The
 * bar diverges from the center: the larger value pushes further out, so the
 * dominant team is read at a glance; the actual values sit at the ends. Reuses
 * the diverging-bar visuals (sop-div-track / -fill / -center).
 */

import type React from 'react';
import { EntityAvatar } from './EntityAvatar';

export interface VersusEntity {
  name: string;
  color: string;
  crop?: string;
  crops?: string[];
}

export interface VersusMetric {
  label: string;
  left: number;
  right: number;
  fmt: (v: number) => string;
}

export const VersusBars: React.FC<{
  left: VersusEntity;
  right: VersusEntity;
  metrics: VersusMetric[];
}> = ({ left, right, metrics }) => (
  <div className="vs">
    <div className="vs-head">
      <span className="vs-head-side">
        <EntityAvatar
          name={left.name}
          color={left.color}
          crop={left.crop}
          crops={left.crops}
        />
        <span className="cmp-name">{left.name}</span>
      </span>
      <span className="vs-head-side vs-head-right">
        <span className="cmp-name">{right.name}</span>
        <EntityAvatar
          name={right.name}
          color={right.color}
          crop={right.crop}
          crops={right.crops}
        />
      </span>
    </div>

    <div className="vs-group">
      {metrics.map((m) => {
        const total = m.left + m.right;
        const lf = total > 0 ? m.left / total : 0.5;
        const rf = total > 0 ? m.right / total : 0.5;
        return (
          <div className="vs-metric" key={m.label}>
            <div className="vs-metric-label">{m.label}</div>
            <div className="vs-metric-bar">
              <span className="vs-num">{m.fmt(m.left)}</span>
              {/* Two-tone halves: your side in accent blue, the opponents in
                  the deeper navy — the header avatars/names and the colored end
                  values say which is which; read the balance off the center. */}
              <div className="sop-div-track">
                <span className="sop-div-center" aria-hidden="true" />
                <span
                  className="sop-div-fill off"
                  style={{ width: `${lf * 50}%`, background: 'var(--accent)' }}
                />
                <span
                  className="sop-div-fill def"
                  style={{
                    width: `${rf * 50}%`,
                    background: 'var(--accent-deep)',
                  }}
                />
              </div>
              <span className="vs-num" style={{ textAlign: 'left' }}>
                {m.fmt(m.right)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);
